import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Row,
  Col,
  Input,
  Badge,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert
} from 'reactstrap';
import {
  FaBoxes,
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
  FaWarehouse,
  FaCalendarAlt,
  FaBarcode,
  FaEllipsisV
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { lotActions } from '../../sagas/lotSlice';
import { formatPrice } from '../../helpers/utility';
import LotForm from './LotForm';
import LotDetails from './LotDetails';
import MouvementStockModal from './MouvementStockModal';
import './LotPage.css';

const LotPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const {
    lots,
    loading,
    error,
    totalPages,
    totalElements,
    currentPage,
    pageSize,
    statistics,
    lotsExpirants
  } = useSelector(state => state.lots);

  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showMouvementModal, setShowMouvementModal] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [filter, setFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [emplacementFilter, setEmplacementFilter] = useState('');
  const [expirationFilter, setExpirationFilter] = useState('');

  useEffect(() => {
    loadLots();
    loadStatistiques();
    loadLotsExpirants();
  }, [currentPage, filter, statutFilter, emplacementFilter, expirationFilter]);

  const loadLots = () => {
    const params = {
      page: currentPage - 1,
      size: pageSize,
      search: filter,
      statut: statutFilter,
      emplacement: emplacementFilter,
      expiration: expirationFilter
    };
    dispatch(lotActions.listRequest(params));
  };

  const loadStatistiques = () => {
    dispatch(lotActions.getStatisticsRequest({}));
  };

  const loadLotsExpirants = () => {
    dispatch(lotActions.getExpiringLotsRequest({ days: 30 }));
  };

  const handleCreateLot = () => {
    setSelectedLot(null);
    setShowModal(true);
  };

  const handleEditLot = (lot) => {
    setSelectedLot(lot);
    setShowModal(true);
  };

  const handleViewDetails = (lot) => {
    setSelectedLot(lot);
    setShowDetailsModal(true);
  };

  const handleMouvementStock = (lot) => {
    setSelectedLot(lot);
    setShowMouvementModal(true);
  };

  const handleDeleteLot = (lot) => {
    Swal.fire({
      title: t('common.confirm'),
      text: t('lot.confirmDelete'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('common.yes'),
      cancelButtonText: t('common.no'),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(lotActions.deleteRequest({
          numeroLot: lot.numeroLot,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('common.deleted'),
              text: t('lot.deleteSuccess'),
            });
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('common.error'),
              text: error.message || t('lot.deleteError'),
            });
          }
        }));
      }
    });
  };

  const handleChangeStatut = (lot, newStatut) => {
    dispatch(lotActions.changeStatutRequest({
      numeroLot: lot.numeroLot,
      statut: newStatut,
      onSuccess: () => {
        Swal.fire({
          icon: 'success',
          title: t('common.updated'),
          text: t('lot.statutUpdated'),
        });
      },
      onError: (error) => {
        Swal.fire({
          icon: 'error',
          title: t('common.error'),
          text: error.message || t('lot.statutUpdateError'),
        });
      }
    }));
  };

  const getStatutBadge = (statut) => {
    const statutConfig = {
      ACTIF: { color: 'success', icon: FaCheckCircle, text: t('lot.actif') },
      EPUISE: { color: 'warning', icon: FaExclamationTriangle, text: t('lot.epuise') },
      RETIRE: { color: 'secondary', icon: FaTimes, text: t('lot.retire') },
      NON_CONFORME: { color: 'danger', icon: FaExclamationTriangle, text: t('lot.nonConforme') }
    };

    const config = statutConfig[statut] || statutConfig.ACTIF;
    const IconComponent = config.icon;

    return (
      <Badge color={config.color} className="d-flex align-items-center">
        <IconComponent className="me-1" size={12} />
        {config.text}
      </Badge>
    );
  };

  const getExpirationStatus = (dateExpiration) => {
    if (!dateExpiration) return null;
    
    const today = new Date();
    const expDate = new Date(dateExpiration);
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return <Badge color="danger">{t('lot.expired')}</Badge>;
    } else if (diffDays <= 7) {
      return <Badge color="warning">{t('lot.expiringSoon', { days: diffDays })}</Badge>;
    } else if (diffDays <= 30) {
      return <Badge color="info">{t('lot.expiringIn', { days: diffDays })}</Badge>;
    }
    return null;
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLot(null);
    loadLots();
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedLot(null);
  };

  const handleCloseMouvementModal = () => {
    setShowMouvementModal(false);
    setSelectedLot(null);
    loadLots();
  };

  if (loading && lots.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="mt-2 text-muted">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="lot-page">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FaBoxes className="text-primary me-2" />
                {t('lot.title')}
              </h4>
              <Button color="primary" onClick={handleCreateLot}>
                <FaPlus className="me-2" />
                {t('lot.new')}
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Statistiques */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="shadow-sm bg-gradient h-100">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    {t('lot.totalLots')}
                  </div>
                  <div className="h3 mb-0 font-weight-bold">
                    {statistics?.totalLots || 0}
                  </div>
                </div>
                <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                  <FaBoxes className="text-primary h3 mb-0" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm bg-gradient h-100">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    {t('lot.lotsActifs')}
                  </div>
                  <div className="h3 mb-0 font-weight-bold">
                    {statistics?.lotsActifs || 0}
                  </div>
                </div>
                <div className="rounded-circle bg-success bg-opacity-10 p-3">
                  <FaCheckCircle className="text-success h3 mb-0" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm bg-gradient h-100">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    {t('lot.lotsExpirants')}
                  </div>
                  <div className="h3 mb-0 font-weight-bold">
                    {statistics?.lotsProchesExpiration || 0}
                  </div>
                </div>
                <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                  <FaExclamationTriangle className="text-warning h3 mb-0" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm bg-gradient h-100">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                    {t('lot.lotsEpuises')}
                  </div>
                  <div className="h3 mb-0 font-weight-bold">
                    {statistics?.lotsEpuises || 0}
                  </div>
                </div>
                <div className="rounded-circle bg-danger bg-opacity-10 p-3">
                  <FaTimes className="text-danger h3 mb-0" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Alertes pour lots expirants */}
      {lotsExpirants.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Alert color="warning">
              <div className="d-flex align-items-center">
                <FaExclamationTriangle className="me-2" />
                <div>
                  <strong>{t('lot.alerteExpiration')}</strong>
                  <div className="mt-1">
                    {lotsExpirants.slice(0, 3).map(lot => (
                      <Badge key={lot.numeroLot} color="warning" className="me-2">
                        {lot.numeroLot} - {lot.articleNom}
                      </Badge>
                    ))}
                    {lotsExpirants.length > 3 && (
                      <span className="text-muted">
                        {t('lot.etAutres', { count: lotsExpirants.length - 3 })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Filtres */}
      <Card className="shadow-sm mb-4">
        <CardBody>
          <Row>
            <Col md={3}>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <FaSearch />
                </span>
                <Input
                  type="text"
                  placeholder={t('lot.searchPlaceholder')}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </div>
            </Col>
            <Col md={2}>
              <Input
                type="select"
                value={statutFilter}
                onChange={(e) => setStatutFilter(e.target.value)}
              >
                <option value="">{t('lot.allStatuts')}</option>
                <option value="ACTIF">{t('lot.actif')}</option>
                <option value="EPUISE">{t('lot.epuise')}</option>
                <option value="RETIRE">{t('lot.retire')}</option>
                <option value="NON_CONFORME">{t('lot.nonConforme')}</option>
              </Input>
            </Col>
            <Col md={2}>
              <Input
                type="text"
                placeholder={t('lot.emplacement')}
                value={emplacementFilter}
                onChange={(e) => setEmplacementFilter(e.target.value)}
              />
            </Col>
                        <Col md={2}>
              <Input
                type="select"
                value={expirationFilter}
                onChange={(e) => setExpirationFilter(e.target.value)}
              >
                <option value="">{t('lot.allExpirations')}</option>
                <option value="expired">{t('lot.expired')}</option>
                <option value="expiring_7">{t('lot.expiring7Days')}</option>
                <option value="expiring_30">{t('lot.expiring30Days')}</option>
                <option value="valid">{t('lot.valid')}</option>
              </Input>
            </Col>
            <Col md={3} className="d-flex gap-2">
              <Button color="primary" onClick={loadLots} size="sm">
                <FaSearch className="me-1" />
                {t('common.search')}
              </Button>
              <Button 
                color="secondary" 
                onClick={() => {
                  setFilter('');
                  setStatutFilter('');
                  setEmplacementFilter('');
                  setExpirationFilter('');
                }} 
                size="sm"
              >
                {t('common.reset')}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Table des lots */}
      <Card className="shadow-sm">
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">{t('common.loading')}</div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover size="sm">
                <thead className="table-light">
                  <tr>
                    <th className="align-middle text-center">
                      <FaBarcode className="me-1" />
                      {t('lot.numeroLot')}
                    </th>
                    <th className="align-middle text-center">{t('lot.article')}</th>
                    <th className="align-middle text-center">
                      <FaCalendarAlt className="me-1" />
                      {t('lot.dateProduction')}
                    </th>
                    <th className="align-middle text-center">
                      <FaCalendarAlt className="me-1" />
                      {t('lot.dateExpiration')}
                    </th>
                    <th className="align-middle text-center">{t('lot.quantiteInitiale')}</th>
                    <th className="align-middle text-center">{t('lot.quantiteDisponible')}</th>
                    <th className="align-middle text-center">
                      <FaWarehouse className="me-1" />
                      {t('lot.emplacement')}
                    </th>
                    <th className="align-middle text-center">{t('lot.statut')}</th>
                    <th className="align-middle text-center">{t('lot.expiration')}</th>
                    <th className="align-middle text-center">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((lot) => (
                    <tr key={lot.numeroLot}>
                      <td className="align-middle text-center">
                        <strong>{lot.numeroLot}</strong>
                      </td>
                      <td className="align-middle">
                        <div>
                          <strong>{lot.article?.nom || lot.articleNom}</strong>
                          <br />
                          <small className="text-muted">
                            {lot.article?.code || lot.articleCode}
                          </small>
                        </div>
                      </td>
                      <td className="align-middle text-center">
                        {lot.dateProduction ? 
                          new Date(lot.dateProduction).toLocaleDateString('fr-FR') : 
                          '-'
                        }
                      </td>
                      <td className="align-middle text-center">
                        {lot.dateExpiration ? 
                          new Date(lot.dateExpiration).toLocaleDateString('fr-FR') : 
                          '-'
                        }
                      </td>
                      <td className="align-middle text-center">
                        <strong>{lot.quantiteInitiale}</strong>
                        <br />
                        <small className="text-muted">{lot.article?.unite || 'U'}</small>
                      </td>
                      <td className="align-middle text-center">
                        <strong className={lot.quantiteDisponible <= 0 ? 'text-danger' : 'text-success'}>
                          {lot.quantiteDisponible}
                        </strong>
                        <br />
                        <small className="text-muted">{lot.article?.unite || 'U'}</small>
                      </td>
                      <td className="align-middle text-center">
                        <Badge color="info" outline>
                          {lot.emplacement || '-'}
                        </Badge>
                      </td>
                      <td className="align-middle text-center">
                        {getStatutBadge(lot.statut)}
                      </td>
                      <td className="align-middle text-center">
                        {getExpirationStatus(lot.dateExpiration)}
                      </td>
                      <td className="align-middle text-center">
                        <div className="d-flex justify-content-center gap-1">
                          <Button
                            color="info"
                            size="sm"
                            onClick={() => handleViewDetails(lot)}
                            title={t('common.view')}
                          >
                            <FaEye />
                          </Button>
                          
                          {lot.statut === 'ACTIF' && (
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => handleMouvementStock(lot)}
                              title={t('lot.mouvementStock')}
                            >
                              <FaWarehouse />
                            </Button>
                          )}

                          <Button
                            color="warning"
                            size="sm"
                            onClick={() => handleEditLot(lot)}
                            title={t('common.edit')}
                          >
                            <FaEdit />
                          </Button>

                          <UncontrolledDropdown>
                            <DropdownToggle caret color="secondary" size="sm">
                              <FaEllipsisV />
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem 
                                onClick={() => handleChangeStatut(lot, 'ACTIF')}
                                disabled={lot.statut === 'ACTIF'}
                              >
                                <FaCheckCircle className="me-2 text-success" />
                                {t('lot.marquerActif')}
                              </DropdownItem>
                              <DropdownItem 
                                onClick={() => handleChangeStatut(lot, 'EPUISE')}
                                disabled={lot.statut === 'EPUISE'}
                              >
                                <FaExclamationTriangle className="me-2 text-warning" />
                                {t('lot.marquerEpuise')}
                              </DropdownItem>
                              <DropdownItem 
                                onClick={() => handleChangeStatut(lot, 'RETIRE')}
                                disabled={lot.statut === 'RETIRE'}
                              >
                                <FaTimes className="me-2 text-secondary" />
                                {t('lot.marquerRetire')}
                              </DropdownItem>
                              <DropdownItem 
                                onClick={() => handleChangeStatut(lot, 'NON_CONFORME')}
                                disabled={lot.statut === 'NON_CONFORME'}
                              >
                                <FaExclamationTriangle className="me-2 text-danger" />
                                {t('lot.marquerNonConforme')}
                              </DropdownItem>
                              <DropdownItem divider />
                              <DropdownItem 
                                onClick={() => handleDeleteLot(lot)}
                                className="text-danger"
                              >
                                <FaTrash className="me-2" />
                                {t('common.delete')}
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {lots.length === 0 && (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        <div className="text-muted">
                          <FaBoxes className="mb-2" size={48} />
                          <div>{t('lot.noLots')}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                {t('common.showing', {
                  start: (currentPage - 1) * pageSize + 1,
                  end: Math.min(currentPage * pageSize, totalElements),
                  total: totalElements
                })}
              </small>
              <div className="d-flex gap-1">
                <Button
                  size="sm"
                  color="outline-primary"
                  disabled={currentPage === 1}
                  onClick={() => dispatch(lotActions.listRequest({ 
                    ...{ page: 0, size: pageSize, search: filter, statut: statutFilter, emplacement: emplacementFilter, expiration: expirationFilter }
                  }))}
                >
                  {t('common.first')}
                </Button>
                <Button
                  size="sm"
                  color="outline-primary"
                  disabled={currentPage === 1}
                  onClick={() => dispatch(lotActions.listRequest({ 
                    ...{ page: currentPage - 2, size: pageSize, search: filter, statut: statutFilter, emplacement: emplacementFilter, expiration: expirationFilter }
                  }))}
                >
                  {t('common.previous')}
                </Button>
                <span className="align-self-center mx-2">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  size="sm"
                  color="outline-primary"
                  disabled={currentPage === totalPages}
                  onClick={() => dispatch(lotActions.listRequest({ 
                    ...{ page: currentPage, size: pageSize, search: filter, statut: statutFilter, emplacement: emplacementFilter, expiration: expirationFilter }
                  }))}
                >
                  {t('common.next')}
                </Button>
                <Button
                  size="sm"
                  color="outline-primary"
                  disabled={currentPage === totalPages}
                  onClick={() => dispatch(lotActions.listRequest({ 
                    ...{ page: totalPages - 1, size: pageSize, search: filter, statut: statutFilter, emplacement: emplacementFilter, expiration: expirationFilter }
                  }))}
                >
                  {t('common.last')}
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Modals */}
      <Modal isOpen={showModal} toggle={handleCloseModal} size="lg">
        <ModalHeader toggle={handleCloseModal}>
          {selectedLot ? t('lot.edit') : t('lot.new')}
        </ModalHeader>
        <ModalBody>
          <LotForm lot={selectedLot} onClose={handleCloseModal} />
        </ModalBody>
      </Modal>

      <Modal isOpen={showDetailsModal} toggle={handleCloseDetailsModal} size="xl">
        <ModalHeader toggle={handleCloseDetailsModal}>
          {t('lot.details')} - {selectedLot?.numeroLot}
        </ModalHeader>
        <ModalBody>
          {selectedLot && <LotDetails lot={selectedLot} />}
        </ModalBody>
      </Modal>

      <Modal isOpen={showMouvementModal} toggle={handleCloseMouvementModal} size="lg">
        <ModalHeader toggle={handleCloseMouvementModal}>
          {t('lot.mouvementStock')} - {selectedLot?.numeroLot}
        </ModalHeader>
        <ModalBody>
          {selectedLot && (
            <MouvementStockModal 
              lot={selectedLot} 
              onClose={handleCloseMouvementModal} 
            />
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default LotPage;

